import styles from './customerDetail.module.css'
import { EmailsTable, Labels, PhonesTable, useDataTypesHooks, useDescribeHooks } from "../../../../../features/data-types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Badge, DetailFieldProps, DetailPanel, FieldType, FieldValue, FormatType, Link, UIPermission, useGetCrumbs } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { VatCondition } from "../../../../administration"
import { AddressesTable, addressHelpers } from "../../../../../features/address"
import { CompanyUserName, useSystemStateHooks } from "../../../../system"
import { Customer, CustomerCategory } from "../../../state/types"
import { useCustomerHooks } from "../../../hooks/customerHooks"
import { useNavigate } from 'react-router-dom'

export const CustomerDetail = () => {
  const { dataType } = useRoutesHooks().parseUrl()
  const customer = dataType! as Customer
  const { emails, phones, mainAddress, deliveryAddresses, creditAccount, creditInFavor = 0, grossIncomeProvinces, ...data } = customer

  const { describeAddress } = addressHelpers
  const { userHasPermission } = useSystemStateHooks()
  const readOnly = !userHasPermission(UIPermission.SAVE_CUSTOMERS) || customer.archived
  const { formatCustomValue, formatPrice, formatDateTime } = useDataTypesHooks()
  const { describeContact } = useCustomerHooks()
  const { getRoutePath } = useRoutesHooks()
  const { describe } = useDescribeHooks()
  const navigate = useNavigate()

  const title = describe(TableName.CUSTOMERS, customer)
  const crumbs = useGetCrumbs(
    'Clientes',
    RoutePath.CUSTOMERS,
    customer.name
  )

  const goToCreditAccount = () => navigate(getRoutePath(RoutePath.CREDIT_ACCOUNTS))

  const fields: DetailFieldProps[] = [
    {
      name: 'name',
      type: FieldType.TEXT,
      label: 'Nombre'
    },
    {
      name: 'identifier',
      type: FieldType.NUMBER,
      label: 'DNI',
      render: (value?: FieldValue) => {
        const format = (value as number).toString().length === 11 ? FormatType.CUIT_CUIL : FormatType.DNI
        return value ? <>{formatCustomValue(value, format)}</> : <Badge label="Vacío" />
      }
    },
    {
      name: 'category',
      type: FieldType.TEXT,
      label: 'Categoría',
      render: (value?: FieldValue) => <>{Labels.customerCategoryLabel(value! as CustomerCategory)}</>
    },
    {
      name: 'customerTypeId',
      type: FieldType.TABLE,
      table: TableName.CUSTOMER_TYPES,
      label: 'Lista de Precios'
    },
    {
      name: 'vatCondition',
      type: FieldType.SELECT,
      label: 'Condición frente al IVA',
      render: (value?: FieldValue) => <>{Labels.vatConditionLabel(value! as VatCondition)}</>
    }
  ]
  if (creditInFavor > 0) {
    const creditField: DetailFieldProps = {
      name: 'creditInFavor',
      type: FieldType.PRICE,
      label: 'Crédito a Favor',
      render: () => <>{formatPrice(creditInFavor)}</>
    }
    fields.push(creditField)
  }
  if (customer.creditAccount) {
    const creditAccountField: DetailFieldProps = {
      name: 'creditAccount',
      type: FieldType.TEXT,
      label: 'Cuenta Corriente',
      render: () => (
        <div className={styles.creditAccount}>
          <span>Crédito Otorgado: {formatPrice(customer?.creditAccount?.creditLimit)}</span>
          <span>Plazo de Pago: {customer?.creditAccount?.creditDays} días</span>
          <Link
            text="Ver Estado"
            icon="card-heading"
            kind="primary"
            onClick={goToCreditAccount}
          />
        </div>
      )
    }
    fields.push(creditAccountField)
  }
  if (customer.mainAddress) {
    const addressField: DetailFieldProps = {
      name: 'mainAddress',
      type: FieldType.TEXT,
      label: 'Domicilio',
      render: () => <>{describeAddress(customer.mainAddress!)}</>
    }
    fields.push(addressField)
  }
  if (customer.emails.length > 0) {
    const emailsField: DetailFieldProps = {
      name: 'emails',
      type: FieldType.TABLE,
      label: 'Emails',
      render: () => (
        <EmailsTable
          rows={customer.emails.map(email => ({ email }))}
          readOnly
        />
      )
    }
    fields.push(emailsField)
  }
  if (customer.phones.length > 0) {
    const phonesField: DetailFieldProps = {
      name: 'phones',
      type: FieldType.TABLE,
      label: 'Teléfonos',
      render: () => (
        <PhonesTable
          rows={customer.phones.map(phone => ({ phoneStr: phone.toString(), phone }))}
          readOnly
        />
      )
    }
    fields.push(phonesField)
  }
  if (customer.deliveryAddresses.length > 0) {
    const deliveryAddressesField: DetailFieldProps = {
      name: 'deliveryAddresses',
      type: FieldType.TABLE,
      label: 'Direcciones de Entrega',
      render: () => (
        <AddressesTable
          rows={customer.deliveryAddresses}
          readOnly
        />
      )
    }
    fields.push(deliveryAddressesField)
  }
  if (customer.contactName || customer.contactPhone) {
    const contactField: DetailFieldProps = {
      name: 'contact',
      type: FieldType.TEXT,
      label: 'Contacto',
      render: () => <>{describeContact(customer)}</>
    }
    fields.push(contactField)
  }
  if (customer.negativeStateUserId) {
    fields.push({
      name: 'negativeStateUserId',
      type: FieldType.TEXT,
      label: 'Archivado por',
      render: () => {
        const dateStr = customer.negativeStateAt ? ` - ${formatDateTime(new Date(customer.negativeStateAt))}` : ''
        return <><CompanyUserName userId={customer.negativeStateUserId!} />{dateStr}</>
      }
    })
  }

  return (<>
    <DetailPanel
      title={title}
      tableName={TableName.CUSTOMERS}
      crumbs={crumbs}
      fields={fields}
      data={data}
      icon='person-fill'
      readOnly={readOnly}
    />
  </>)
}
